import React, { useCallback, useState, useRef } from 'react';
import { User as UserIcon, Trash2, Globe, Bell, Shield, Camera, CheckCircle2, AlertTriangle, Download, Clock, Mail, Smartphone, ChevronRight, Eye } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Badge } from '../ui/Badge';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
import { User } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';
interface SettingsTabProps {
  theme: ZodiacTheme;
  user: User | null;
  avatarUrl?: string | null;
  onAvatarChange?: (url: string | null) => void;
}
export function SettingsTab({
  theme,
  user,
  avatarUrl: externalAvatarUrl,
  onAvatarChange
}: SettingsTabProps) {
  const {
    t
  } = useLanguage();
  const {
    showToast
  } = useToast();
  const SECTIONS = [{
    id: 'profile',
    label: t('userdash.settings.profile'),
    icon: <UserIcon className="w-3.5 h-3.5" data-id="element-19381" />
  }, {
    id: 'region',
    label: t('userdash.settings.region'),
    icon: <Globe className="w-3.5 h-3.5" data-id="element-19382" />
  }, {
    id: 'notifications',
    label: t('userdash.settings.notifications'),
    icon: <Bell className="w-3.5 h-3.5" data-id="element-19383" />
  }, {
    id: 'data',
    label: t('userdash.settings.data'),
    icon: <Shield className="w-3.5 h-3.5" data-id="element-19384" />
  }];
  const activityLog = [{
    action: t('userdash.settings.loginAction'),
    device: 'Mac OS · Safari',
    date: 'Сегодня, 10:15',
    ip: '192.168.1.1'
  }, {
    action: t('userdash.settings.emailChange'),
    device: 'Mac OS · Safari',
    date: 'Вчера, 14:30',
    ip: '192.168.1.1'
  }, {
    action: t('userdash.settings.orderPayment'),
    device: 'iPhone · Safari',
    date: '12 Окт, 09:20',
    ip: '10.0.0.5'
  }, {
    action: t('userdash.settings.passwordChange'),
    device: 'Mac OS · Chrome',
    date: '10 Окт, 18:00',
    ip: '192.168.1.1'
  }, {
    action: t('userdash.settings.loginAction'),
    device: 'iPhone · Safari',
    date: '08 Окт, 12:45',
    ip: '10.0.0.5'
  }];
  const [firstName, setFirstName] = useState(user?.name?.split(' ')[0] || '');
  const [lastName, setLastName] = useState(user?.name?.split(' ').slice(1).join(' ') || '');
  const [email, setEmail] = useState(user?.email || '');
  const [phone, setPhone] = useState('');
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [showVerifyPhone, setShowVerifyPhone] = useState(false);
  const [verifyCode, setVerifyCode] = useState('');
  const [language, setLanguage] = useState('ru');
  const [timezone, setTimezone] = useState('Europe/Moscow');
  const [dateFormat, setDateFormat] = useState('DD.MM.YYYY');
  // Notifications — Email
  const [emailOrders, setEmailOrders] = useState(true);
  const [emailReports, setEmailReports] = useState(true);
  const [emailMarketing, setEmailMarketing] = useState(false);
  // Notifications — Push
  const [pushMessages, setPushMessages] = useState(false);
  const [pushConsultations, setPushConsultations] = useState(true);
  // Reminders
  const [remind24h, setRemind24h] = useState(true);
  const [remind1h, setRemind1h] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [showActivity, setShowActivity] = useState(false);
  const [activeSection, setActiveSection] = useState('profile');
  const avatarUrl = externalAvatarUrl ?? null;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      showToast('Файл слишком большой. Максимум 2MB');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
      showToast('Поддерживаются только JPG, PNG и GIF');
      return;
    }
    const url = URL.createObjectURL(file);
    onAvatarChange?.(url);
    showToast(t('userdash.settings.saved'));
  };
  const autoSave = useCallback((label?: string) => {
    showToast(label || t('userdash.settings.saved'));
  }, [showToast, t]);
  const Toggle = ({
    checked,
    onChange,
    disabled = false
  }: {
    checked: boolean;
    onChange: (v: boolean) => void;
    disabled?: boolean;
  }) => <button onClick={() => {
    if (!disabled) {
      onChange(!checked);
      autoSave();
    }
  }} disabled={disabled} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors shrink-0 ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'} ${checked ? 'bg-indigo-600' : 'bg-slate-200'}`} data-id="element-19385">
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm ${checked ? 'translate-x-6' : 'translate-x-1'}`} data-id="element-19386" />
    </button>;
  const NotifRow = ({
    label,
    desc,
    checked,
    onChange,
    disabled
  }: {
    label: string;
    desc: string;
    checked: boolean;
    onChange: (v: boolean) => void;
    disabled?: boolean;
  }) => <div className="flex items-center justify-between gap-3 py-2.5" data-id="element-19387">
      <div className="min-w-0" data-id="element-19388">
        <div className="text-sm font-medium text-slate-900 flex items-center gap-2" data-id="element-19389">
          {label}
          {disabled && <Badge variant="neutral" size="sm" className="text-[9px] py-0 h-4" data-id="element-19390">
              Скоро
            </Badge>}
        </div>
        <div className="text-xs text-slate-500 mt-0.5" data-id="element-19391">{desc}</div>
      </div>
      <Toggle checked={checked} onChange={onChange} disabled={disabled} data-id="element-19392" />
    </div>;
  const handleVerifyPhone = () => {
    if (verifyCode === '1234' || verifyCode.length === 4) {
      setPhoneVerified(true);
      setShowVerifyPhone(false);
      setVerifyCode('');
      showToast('Телефон подтверждён');
    }
  };
  const handleDeleteAccount = () => {
    if (deletePassword.length >= 6) {
      showToast('Аккаунт удалён');
      setShowDeleteConfirm(false);
      setDeletePassword('');
    }
  };
  const scrollTo = (id: string) => {
    setActiveSection(id);
    document.getElementById(`settings-${id}`)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };
  return <div className="space-y-5 max-w-3xl pb-8" data-id="element-19393">
      {/* Header */}
      <div data-id="element-19394">
        <h2 className="text-xl sm:text-2xl font-bold text-slate-900" data-id="element-19395">
          {t('userdash.settings.title')}
        </h2>
        <p className="text-xs text-slate-500 mt-0.5" data-id="element-19396">
          {t('userdash.settings.subtitle')}
        </p>
      </div>

      {/* Section Nav */}
      <div className="flex items-center gap-1.5 overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] pb-0.5" data-id="element-19397">
        {SECTIONS.map(s => <button key={s.id} onClick={() => scrollTo(s.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors shrink-0 ${activeSection === s.id ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-19398">
            {s.icon} {s.label}
          </button>)}
      </div>

      {/* SECTION 1: Profile */}
      <div id="settings-profile" className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-5" data-id="element-19399">
        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-19400">
          <UserIcon className="w-4 h-4 text-slate-400" data-id="element-19401" />{' '}
          {t('userdash.settings.profile')}
        </h3>

        {/* Avatar */}
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif" className="hidden" onChange={handleAvatarChange} data-id="element-19402" />
        <div className="flex items-center gap-4" data-id="element-19403">
          <div onClick={() => fileInputRef.current?.click()} className={`w-14 h-14 rounded-full ${avatarUrl ? '' : `bg-gradient-to-br ${theme.gradient}`} flex items-center justify-center text-xl text-white font-bold relative group shrink-0 cursor-pointer overflow-hidden`} data-id="element-19404">
            {avatarUrl ? <img src={avatarUrl} alt="Аватар" className="w-full h-full object-cover" data-id="element-19405" /> : firstName.charAt(0) || 'U'}
            <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-19406">
              <Camera className="w-5 h-5 text-white" data-id="element-19407" />
            </div>
          </div>
          <div data-id="element-19408">
            <Button variant="outline" size="sm" className="text-xs" onClick={() => fileInputRef.current?.click()} data-id="element-19409">
              {t('userdash.settings.changeAvatar')}
            </Button>
            <p className="text-[10px] text-slate-400 mt-1" data-id="element-19410">
              JPG, PNG. Макс. 2MB
            </p>
          </div>
        </div>

        {/* Name */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-19411">
          <Input label={t('userdash.settings.name')} value={firstName} onChange={e => setFirstName(e.target.value)} onBlur={() => autoSave()} data-id="element-19412" />
          <Input label="Фамилия" value={lastName} onChange={e => setLastName(e.target.value)} onBlur={() => autoSave()} data-id="element-19413" />
        </div>

        {/* Email */}
        <div data-id="element-19414">
          <div className="flex justify-between items-center mb-1" data-id="element-19415">
            <label className="text-xs font-medium text-slate-700" data-id="element-19416">
              {t('userdash.settings.email')}
            </label>
            <Badge variant="success" size="sm" className="text-[9px] py-0 h-4 px-1.5" data-id="element-19417">
              <CheckCircle2 className="w-3 h-3 mr-0.5" data-id="element-19418" /> Подтверждён
            </Badge>
          </div>
          <Input type="email" value={email} onChange={e => setEmail(e.target.value)} onBlur={() => autoSave()} data-id="element-19419" />
        </div>

        {/* Phone */}
        <div data-id="element-19420">
          <div className="flex justify-between items-center mb-1" data-id="element-19421">
            <label className="text-xs font-medium text-slate-700" data-id="element-19422">
              {t('userdash.settings.phone')}
            </label>
            {phoneVerified ? <Badge variant="success" size="sm" className="text-[9px] py-0 h-4 px-1.5" data-id="element-19423">
                <CheckCircle2 className="w-3 h-3 mr-0.5" data-id="element-19424" /> Подтверждён
              </Badge> : phone ? <button onClick={() => {
            setShowVerifyPhone(true);
            showToast('SMS-код отправлен');
          }} className="text-[10px] font-medium text-indigo-600 hover:underline" data-id="element-19425">
                Подтвердить →
              </button> : <span className="text-[10px] text-slate-400" data-id="element-19426">Не указан</span>}
          </div>
          <Input type="tel" value={phone} onChange={e => setPhone(e.target.value)} placeholder="+7 (___) ___-__-__" onBlur={() => autoSave()} data-id="element-19427" />
          {showVerifyPhone && !phoneVerified && <div className="flex gap-2 mt-2" data-id="element-19428">
              <input type="text" maxLength={4} value={verifyCode} onChange={e => setVerifyCode(e.target.value.replace(/\D/g, ''))} placeholder="Код из SMS" className="flex-1 px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-indigo-500" data-id="element-19429" />
              <Button size="sm" className="text-xs px-3" onClick={handleVerifyPhone} disabled={verifyCode.length < 4} data-id="element-19430">
                Подтвердить
              </Button>
            </div>}
        </div>
      </div>

      {/* SECTION 2: Region */}
      <div id="settings-region" className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-4" data-id="element-19431">
        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-19432">
          <Globe className="w-4 h-4 text-slate-400" data-id="element-19433" />{' '}
          {t('userdash.settings.region')}
        </h3>

        <div data-id="element-19434">
          <label className="text-xs font-medium text-slate-700 mb-1.5 block" data-id="element-19435">
            {t('userdash.settings.language')}
          </label>
          <div className="flex gap-2" data-id="element-19436">
            {[{
            id: 'ru',
            label: 'Русский'
          }, {
            id: 'en',
            label: 'English'
          }, {
            id: 'es',
            label: 'Español'
          }].map(l => <button key={l.id} onClick={() => {
            setLanguage(l.id);
            autoSave('Язык изменён');
          }} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${language === l.id ? 'bg-indigo-50 border-indigo-300 text-indigo-700' : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-19437">
                {l.label}
              </button>)}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-19438">
          <div data-id="element-19439">
            <div className="flex justify-between items-center mb-1" data-id="element-19440">
              <label className="text-xs font-medium text-slate-700" data-id="element-19441">
                {t('userdash.settings.timezone')}
              </label>
              <button onClick={() => {
              const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
              setTimezone(tz);
              autoSave('Часовой пояс определён автоматически');
            }} className="text-[10px] text-indigo-600 hover:underline" data-id="element-19442">
                Определить авто
              </button>
            </div>
            <select value={timezone} onChange={e => {
            setTimezone(e.target.value);
            autoSave();
          }} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-indigo-500" data-id="element-19443">
              <option value="Europe/Moscow" data-id="element-19444">Москва (UTC+3)</option>
              <option value="Europe/London" data-id="element-19445">Лондон (UTC+0)</option>
              <option value="Asia/Dubai" data-id="element-19446">Дубай (UTC+4)</option>
              <option value="Asia/Almaty" data-id="element-19447">Алматы (UTC+5)</option>
            </select>
            <p className="text-[10px] text-slate-400 mt-1" data-id="element-19448">
              Для расчёта времени консультаций и транзитов
            </p>
          </div>
          <div data-id="element-19449">
            <label className="text-xs font-medium text-slate-700 mb-1 block" data-id="element-19450">
              Формат даты
            </label>
            <select value={dateFormat} onChange={e => {
            setDateFormat(e.target.value);
            autoSave();
          }} className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-xs focus:outline-none focus:border-indigo-500" data-id="element-19451">
              <option value="DD.MM.YYYY" data-id="element-19452">31.12.2024</option>
              <option value="MM/DD/YYYY" data-id="element-19453">12/31/2024</option>
              <option value="YYYY-MM-DD" data-id="element-19454">2024-12-31</option>
            </select>
          </div>
        </div>
      </div>

      {/* SECTION 3: Notifications */}
      <div id="settings-notifications" className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-4" data-id="element-19455">
        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-19456">
          <Bell className="w-4 h-4 text-slate-400" data-id="element-19457" />{' '}
          {t('userdash.settings.notifications')}
        </h3>

        {/* Email */}
        <div data-id="element-19458">
          <div className="flex items-center gap-1.5 mb-2" data-id="element-19459">
            <Mail className="w-3.5 h-3.5 text-slate-400" data-id="element-19460" />
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-19461">
              Email
            </span>
          </div>
          <div className="divide-y divide-slate-100" data-id="element-19462">
            <NotifRow label="Статусы заказов" desc="Изменение статуса заказов и оплат" checked={emailOrders} onChange={setEmailOrders} data-id="element-19463" />
            <NotifRow label="Готовые отчёты" desc="Когда PDF-отчёт сгенерирован" checked={emailReports} onChange={setEmailReports} data-id="element-19464" />
            <NotifRow label="Маркетинг" desc="Акции и специальные предложения" checked={emailMarketing} onChange={setEmailMarketing} data-id="element-19465" />
          </div>
        </div>

        {/* Push */}
        <div data-id="element-19466">
          <div className="flex items-center gap-1.5 mb-2" data-id="element-19467">
            <Smartphone className="w-3.5 h-3.5 text-slate-400" data-id="element-19468" />
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-19469">
              Push-уведомления
            </span>
          </div>
          <div className="divide-y divide-slate-100" data-id="element-19470">
            <NotifRow label="Новые сообщения" desc="Сообщения от астрологов и поддержки" checked={pushMessages} onChange={setPushMessages} disabled data-id="element-19471" />
            <NotifRow label="Консультации" desc="Напоминания о предстоящих сессиях" checked={pushConsultations} onChange={setPushConsultations} disabled data-id="element-19472" />
          </div>
        </div>

        {/* Reminders */}
        <div data-id="element-19473">
          <div className="flex items-center gap-1.5 mb-2" data-id="element-19474">
            <Clock className="w-3.5 h-3.5 text-slate-400" data-id="element-19475" />
            <span className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-19476">
              Напоминания
            </span>
          </div>
          <div className="divide-y divide-slate-100" data-id="element-19477">
            <NotifRow label="За 24 часа" desc="Напоминание за сутки до консультации" checked={remind24h} onChange={setRemind24h} data-id="element-19478" />
            <NotifRow label="За 1 час" desc="Напоминание за час до начала" checked={remind1h} onChange={setRemind1h} data-id="element-19479" />
          </div>
        </div>
      </div>

      {/* SECTION 4: Data Management */}
      <div id="settings-data" className="bg-white border border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm space-y-5" data-id="element-19480">
        <h3 className="font-bold text-slate-900 text-sm flex items-center gap-2" data-id="element-19481">
          <Shield className="w-4 h-4 text-slate-400" data-id="element-19482" />{' '}
          {t('userdash.settings.data')}
        </h3>

        {/* Export */}
        <div className="flex items-center justify-between gap-3 pb-4 border-b border-slate-100" data-id="element-19483">
          <div data-id="element-19484">
            <div className="text-sm font-medium text-slate-900" data-id="element-19485">
              Экспорт данных
            </div>
            <div className="text-xs text-slate-500 mt-0.5" data-id="element-19486">
              Архив с отчётами и историей заказов
            </div>
          </div>
          <Button variant="outline" size="sm" className="text-xs shrink-0" onClick={() => showToast('Запрос на экспорт отправлен на email')} data-id="element-19487">
            <Download className="w-3.5 h-3.5 mr-1" data-id="element-19488" /> Скачать
          </Button>
        </div>

        {/* Activity Log */}
        <div data-id="element-19489">
          <button onClick={() => setShowActivity(!showActivity)} className="flex items-center justify-between w-full text-left" data-id="element-19490">
            <div data-id="element-19491">
              <div className="text-sm font-medium text-slate-900" data-id="element-19492">
                История действий
              </div>
              <div className="text-xs text-slate-500 mt-0.5" data-id="element-19493">
                Входы, изменения и операции
              </div>
            </div>
            <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${showActivity ? 'rotate-90' : ''}`} data-id="element-19494" />
          </button>
          {showActivity && <div className="mt-3 space-y-1.5" data-id="element-19495">
              {activityLog.map((a, i) => <div key={i} className="flex items-center gap-3 py-2 px-3 bg-slate-50 rounded-lg" data-id="element-19496">
                  <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" data-id="element-19497" />
                  <div className="flex-1 min-w-0" data-id="element-19498">
                    <div className="text-xs font-medium text-slate-900 truncate" data-id="element-19499">
                      {a.action}
                    </div>
                    <div className="text-[10px] text-slate-400" data-id="element-19500">
                      {a.device} · {a.ip}
                    </div>
                  </div>
                  <span className="text-[10px] text-slate-400 shrink-0" data-id="element-19501">
                    {a.date}
                  </span>
                </div>)}
            </div>}
        </div>

        {/* Delete Account */}
        <div className="pt-4 border-t border-slate-100" data-id="element-19502">
          <div className="text-sm font-medium text-slate-900 mb-0.5" data-id="element-19503">
            Удаление аккаунта
          </div>
          <div className="text-xs text-slate-500 mb-3" data-id="element-19504">
            Все данные, отчёты и история будут удалены без возможности
            восстановления.
          </div>

          {!showDeleteConfirm ? <button onClick={() => setShowDeleteConfirm(true)} className="text-xs font-medium text-rose-600 hover:text-rose-700 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-rose-50 hover:bg-rose-100 transition-colors" data-id="element-19505">
              <Trash2 className="w-3.5 h-3.5" data-id="element-19506" /> Удалить аккаунт
            </button> : <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 space-y-3" data-id="element-19507">
              <div className="flex items-start gap-2" data-id="element-19508">
                <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" data-id="element-19509" />
                <div data-id="element-19510">
                  <div className="text-xs font-bold text-rose-900" data-id="element-19511">
                    Подтвердите удаление
                  </div>
                  <div className="text-[11px] text-rose-700 mt-0.5" data-id="element-19512">
                    Введите пароль для подтверждения.
                  </div>
                </div>
              </div>
              <div className="flex gap-2" data-id="element-19513">
                <input type="password" value={deletePassword} onChange={e => setDeletePassword(e.target.value)} placeholder="Ваш пароль" className="flex-1 px-3 py-1.5 bg-white border border-rose-200 rounded-lg text-xs focus:outline-none focus:border-rose-500" data-id="element-19514" />
                <Button variant="outline" size="sm" className="text-xs" onClick={() => {
              setShowDeleteConfirm(false);
              setDeletePassword('');
            }} data-id="element-19515">
                  Отмена
                </Button>
                <Button size="sm" className="text-xs bg-rose-600 hover:bg-rose-700 text-white border-none" disabled={deletePassword.length < 6} onClick={handleDeleteAccount} data-id="element-19516">
                  Удалить
                </Button>
              </div>
            </div>}
        </div>
      </div>
    </div>;
}